import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { Button, Flex, H3, Loading, P } from '@decisiv/ui-components';
import { t } from '@lingui/macro';
import History from '@decisiv/iconix/lib/components/History';
import uniq from 'lodash/uniq';
import { flattenHistories, formatValue } from './utils';
import FilterBar from './FilterBar';
import HistoryTable from './HistoryTable';
import { EmptyStateWrapper, HistoryCount, LoadMoreWrapper } from './style';
import localCache from '../../utils/localCache';
import { changeHistoryFiltersKey } from '../../utils/storageKeys';
import {
  daysAfter,
  daysBefore,
  isAfter,
  isBefore,
  toDate,
} from '../../utils/dates';

function matchesSearch(row, term) {
  if (!term) return true;
  const lower = term.toLowerCase();
  return (
    row.fieldName.toLowerCase().includes(lower) ||
    row.author.toLowerCase().includes(lower) ||
    formatValue(row.from).toLowerCase().includes(lower) ||
    formatValue(row.to).toLowerCase().includes(lower)
  );
}

function readSavedFilters(key) {
  return localCache.get(key, {}) || {};
}

export default function ChangeHistory({
  histories,
  integration,
  location = {},
  locked = false,
  title = undefined,
  loading = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore = undefined,
  pageSize = 10,
  onPageSizeChange = undefined,
  mode = undefined,
  onModeChange = undefined,
  comboInput = '',
  comboOptions = [],
  comboLoading = false,
  onComboInputChange = undefined,
  onComboValueChange = undefined,
}) {
  const storageKey = changeHistoryFiltersKey(
    integration?.id,
    locked ? location?.id : null,
  );

  const [searchTerm, setSearchTerm] = useState(
    () => readSavedFilters(storageKey).searchTerm || '',
  );
  const [activeAuthors, setActiveAuthors] = useState(
    () => readSavedFilters(storageKey).activeAuthors || [],
  );
  const [activeCategories, setActiveCategories] = useState(
    () => readSavedFilters(storageKey).activeCategories || [],
  );
  const [dateRange, setDateRange] = useState(() => {
    const saved = readSavedFilters(storageKey).dateRange;
    return saved ? saved.map((d) => new Date(d)) : undefined;
  });
  const [expandedRows, setExpandedRows] = useState([]);

  useEffect(() => {
    if (!storageKey) return;
    localCache.set(storageKey, {
      searchTerm,
      activeAuthors,
      activeCategories,
      dateRange,
    });
  }, [storageKey, searchTerm, activeAuthors, activeCategories, dateRange]);

  const allEntries = Array.isArray(histories)
    ? histories
    : Object.values(histories);

  const fallbackSrmAccountId = location?.attributes?.srm_account_id;
  const allRows = useMemo(
    () => flattenHistories(allEntries, fallbackSrmAccountId),
    [allEntries, fallbackSrmAccountId],
  );

  const authors = useMemo(() => uniq(allRows.map((r) => r.author)), [allRows]);
  const categories = useMemo(
    () => uniq(allRows.map((r) => r.category)),
    [allRows],
  );

  const filteredRows = useMemo(() => {
    let rows = allRows;
    if (activeAuthors.length > 0) {
      rows = rows.filter((r) => activeAuthors.includes(r.author));
    }
    if (activeCategories.length > 0) {
      rows = rows.filter((r) => activeCategories.includes(r.category));
    }
    if (dateRange) {
      rows = rows.filter((r) =>
        isAfter(toDate(r.date), daysBefore(dateRange[0], 1)),
      );
      rows = rows.filter((r) =>
        isBefore(toDate(r.date), daysAfter(dateRange[1], 1)),
      );
    }
    if (searchTerm) {
      rows = rows.filter((r) => matchesSearch(r, searchTerm));
    }
    return rows;
  }, [allRows, activeAuthors, activeCategories, dateRange, searchTerm]);

  const handleToggleAuthor = useCallback((author) => {
    setActiveAuthors((prev) =>
      prev.includes(author)
        ? prev.filter((a) => a !== author)
        : [...prev, author],
    );
  }, []);

  const handleToggleCategory = useCallback((category) => {
    setActiveCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  }, []);

  const handleToggleRow = useCallback((rowKey) => {
    setExpandedRows((prev) =>
      prev.includes(rowKey)
        ? prev.filter((k) => k !== rowKey)
        : [...prev, rowKey],
    );
  }, []);

  const handleLoadMore = useCallback(() => {
    if (onLoadMore) onLoadMore();
  }, [onLoadMore]);

  const displayedRows = filteredRows;
  const internalHasMore = hasMore;

  const filterBar = (
    <FilterBar
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      authors={authors}
      activeAuthors={activeAuthors}
      onToggleAuthor={handleToggleAuthor}
      categories={categories}
      activeCategories={activeCategories}
      onToggleCategory={handleToggleCategory}
      dateRange={dateRange}
      onDateRangeChange={setDateRange}
      resultCount={displayedRows.length}
      totalCount={filteredRows.length}
      pageSize={pageSize}
      onPageSizeChange={onPageSizeChange}
      locked={locked}
      mode={mode}
      onModeChange={onModeChange}
      comboInput={comboInput}
      comboOptions={comboOptions}
      comboLoading={comboLoading}
      onComboInputChange={onComboInputChange}
      onComboValueChange={onComboValueChange}
    />
  );

  const bodyContent = () => {
    if (loading) {
      return (
        <Flex justifyContent="center" padding={2}>
          <Loading size="large" />
        </Flex>
      );
    }
    if (mode === 'location' && !locked && !location?.id) {
      return (
        <EmptyStateWrapper>
          <Flex style={{ color: 'var(--if-ink-2)' }}>
            <History size="large" />
          </Flex>
          <P color="alaskanHusky">{t`Search for a location to view change history`}</P>
        </EmptyStateWrapper>
      );
    }
    if (allEntries.length === 0) {
      return (
        <EmptyStateWrapper>
          <Flex style={{ color: 'var(--if-ink-2)' }}>
            <History size="large" />
          </Flex>
          <P color="alaskanHusky">{t`No changes recorded yet.`}</P>
        </EmptyStateWrapper>
      );
    }
    return (
      <>
        <HistoryTable
          rows={displayedRows}
          expandedRows={expandedRows}
          onToggleRow={handleToggleRow}
        />
        {internalHasMore && (
          <LoadMoreWrapper>
            {loadingMore ? (
              <Loading size="small" />
            ) : (
              <Button
                text={t`Load more`}
                size="small"
                kind="secondary"
                variant="ghost"
                onClick={handleLoadMore}
              />
            )}
          </LoadMoreWrapper>
        )}
      </>
    );
  };

  return (
    <>
      <Flex alignItems="center" marginBottom={1}>
        <H3>{title || t`History`}</H3>
        <HistoryCount>({loading ? 0 : displayedRows.length})</HistoryCount>
      </Flex>
      {filterBar}
      {bodyContent()}
    </>
  );
}
ChangeHistory.propTypes = {
  histories: PropTypes.oneOfType([PropTypes.object, PropTypes.array])
    .isRequired,
  integration: PropTypes.oneOfType([PropTypes.object]).isRequired,
  location: PropTypes.oneOfType([PropTypes.object]),
  locked: PropTypes.bool,
  title: PropTypes.string,
  loading: PropTypes.bool,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
  onLoadMore: PropTypes.func,
  pageSize: PropTypes.number,
  onPageSizeChange: PropTypes.func,
  mode: PropTypes.string,
  onModeChange: PropTypes.func,
  comboInput: PropTypes.string,
  comboOptions: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
  comboLoading: PropTypes.bool,
  onComboInputChange: PropTypes.func,
  onComboValueChange: PropTypes.func,
};
